// The ⌘K palette — TODO T36 (2 Sep 2026), design doc § T36.6.
//
// One field across candidates, searches and clients, grouped, keyboard-navigable, Enter opens.
// DETERMINISTIC: the service matches substrings and nothing else (services/bff/src/routes/
// search.ts), so a name finds the same rows every time and the palette never waits on a model.
// The list and the keyboard are pure (palette.ts); this file draws them and forwards the keys.
//
// The top bar's field promised "candidates, searches, clients — or ask kadmon" from the mockup
// onwards, and T30 narrowed it to the one thing it could do (search the candidate list) rather
// than render a promise it could not keep. This is the second thing. The third — a sentence in
// the same field going to the find panel — is T35's (3 Sep 2026): the ask row at the foot of the
// list, first when the term reads as a sentence, so Enter opens the panel (app/VeraPanel.tsx)
// with the question already asked. The palette still sends the model nothing itself.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMe } from '../api/me.ts';
import { usePalette } from '../api/search.ts';
import { Avatar, Icon, VeraOrb, type IconName, FirmName } from '../ui/atoms.tsx';
import { Failed } from '../ui/states.tsx';
import { useScrollLock } from './scrollLock.ts';
import {
  ASK_HINT,
  askItem,
  clampSelection,
  flattenPalette,
  isSentence,
  moveSelection,
  type PaletteItem,
} from './palette.ts';
import '../styles/screens/palette.css';

/** A keystroke's worth of quiet before asking; a palette answers per word, not per letter. */
const DEBOUNCE_MS = 150;

const GROUP_ICON: Record<PaletteItem['group'], IconName> = {
  Candidates: 'users',
  Searches: 'inbox',
  Clients: 'building',
  Connections: 'link',
  More: 'search',
  'Ask kadmon': 'sparkle',
};

export function Palette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [asked, setAsked] = useState('');
  const [selected, setSelected] = useState(0);
  const result = usePalette(asked);
  const me = useMe();
  // Until `/me` answers, assume the server can find: the row then opens a drawer that says so.
  const findEnabled = me.data ? me.data.findEnabled : true;

  // Mounted only while open, so the page is held for exactly that long (app/scrollLock.ts).
  useScrollLock(true);

  useEffect(() => {
    const timer = setTimeout(() => setAsked(q), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    input.current?.focus();
  }, []);

  const items = useMemo(
    () => flattenPalette(result.data, asked, findEnabled),
    [result.data, asked, findEnabled],
  );
  const index = clampSelection(selected, items.length);

  function open(item: PaletteItem) {
    if (item.disabled) return;
    onClose();
    void navigate(item.to, item.state ? { state: item.state } : undefined);
  }

  function onKey(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelected(moveSelection(index, 1, items.length));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelected(moveSelection(index, -1, items.length));
        break;
      case 'Enter': {
        const item = items[index];
        if (item) {
          event.preventDefault();
          open(item);
        } else if (findEnabled && isSentence(q)) {
          // A sentence typed faster than the lookup answers still goes to kadmon on Enter.
          event.preventDefault();
          open(askItem(q.trim(), true));
        }
        break;
      }
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
    }
  }

  // Group headings are drawn where the group changes; the list itself is flat so the arrow keys
  // have one index to move.
  let lastGroup: PaletteItem['group'] | null = null;

  return (
    <>
      <div className="palette-scrim" onClick={onClose} aria-hidden="true" />
      <div className="palette" role="dialog" aria-label="Search">
        <div className="palette__field">
          <Icon name="search" size={15} />
          <input
            ref={input}
            type="text"
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              setSelected(0);
            }}
            onKeyDown={onKey}
            placeholder="Search candidates, searches, clients, or ask kadmon"
            aria-label="Search candidates, searches and clients, or ask kadmon to find candidates"
            aria-activedescendant={items[index] ? `palette-${items[index].key}` : undefined}
            autoComplete="off"
            spellCheck={false}
          />
          <span className="kbd">esc</span>
        </div>

        {result.isError && (
          <div className="palette__group">
            <Failed what="the search" error={result.error} />
          </div>
        )}

        {asked.trim() !== '' && items.length > 0 && (
          <div role="listbox" aria-label="Results">
            {items.map((item, i) => {
              const heading = item.group !== lastGroup ? item.group : null;
              lastGroup = item.group;
              return (
                <div key={item.key} className={heading ? 'palette__group' : 'palette__continue'}>
                  {heading && <div className="micro palette__heading">{heading}</div>}
                  <button
                    type="button"
                    id={`palette-${item.key}`}
                    role="option"
                    aria-selected={i === index}
                    className={i === index ? 'palette__item palette__item--on' : 'palette__item'}
                    aria-disabled={item.disabled}
                    onMouseEnter={() => setSelected(i)}
                    onClick={() => open(item)}
                  >
                    {item.avatar ? (
                      <Avatar name={item.avatar} size={22} />
                    ) : item.group === 'Ask kadmon' ? (
                      <VeraOrb size={12} active={false} />
                    ) : (
                      <span className="palette__icon">
                        <Icon name={GROUP_ICON[item.group]} size={12} />
                      </span>
                    )}
                    <span style={{ minWidth: 0 }}>
                      <span className="palette__title">{item.title}</span>
                      <span className="row-meta">{item.sub}</span>
                    </span>
                    <span className="micro">{i === index ? 'enter' : ''}</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {asked.trim() === '' && (
          <div className="palette__group">
            <p className="micro palette__hint">
              A name, an employer, a title, a location, a number, a tag, a note, or a search or a
              client by name. A sentence goes to <FirmName />. ↑ ↓ to move, Enter to open.
            </p>
          </div>
        )}

        {/* The find's door (T35), where the mockup draws it. With a term typed the same row is in
            the list above, so the keys reach it; empty, it is this one. */}
        {asked.trim() === '' && (
          <div className="palette__group" style={{ background: 'var(--bg-2)' }}>
            <button
              type="button"
              className="palette__item"
              aria-disabled={!findEnabled}
              onClick={() => open(askItem('', findEnabled))}
            >
              <VeraOrb size={12} active={false} />
              <span style={{ minWidth: 0 }}>
                <span className="palette__title">
                  {findEnabled ? 'Ask kadmon' : 'Ask kadmon (not set up on this server)'}
                </span>
                <span className="row-meta">{ASK_HINT}</span>
              </span>
              <Icon name="sparkle" size={12} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}
